import _ from 'lodash';

const analyticsTypes = {
  CAMPAIGN_CLAIM_SHOW: 'campaign_Claim_Show',
  CAMPAIGN_CLAIM_CLICK: 'campaign_Claim_Click',
  CAMPAIGN_CLAIM_CLAIM: 'campaign_Claim_Claim',
  CAMPAIGN_CLAIM_DISMISS: 'campaign_Claim_Dismiss',
  CAMPAIGN_CLAIM_COLLECT_INFO_SHOW: 'campaign_Claim_CollectInfo_Show',
  CAMPAIGN_CLAIM_COLLECT_INFO_CLICK: 'campaign_Claim_CollectInfo_Click',
  CAMPAIGN_CLAIM_COLLECT_INFO_SUBMIT: 'campaign_Claim_CollectInfo_Submit',
  CAMPAIGN_CLAIM_COLLECT_INFO_DISMISS: 'campaign_Claim_CollectInfo_Dismiss',
  CAMPAIGN_CREATIVE_START: 'campaign_Creative_Open',
  CAMPAIGN_CREATIVE_STOP: 'campaign_Creative_Close',
  CAMPAIGN_CREATIVE_ADD_TO_CART: 'campaign_Creative_AddToCart',
  CAMPAIGN_CROSS_SELL_SHOW: 'campaign_CrossSell_Show',
  CAMPAIGN_CROSS_SELL_CLICK: 'campaign_CrossSell_Click',
  CAMPAIGN_DEAL_VIEW: 'campaign_Deals_View',
  CAMPAIGN_DEAL_CLICK: 'campaign_Deals_Click',
  UPSELL_SHOW: 'upsell_Show',
  UPSELL_CLOSE: 'upsell_Close',
  UPSELL_ADD_TO_CART: 'upsell_AddToCart',
  UPSELL_SEARCH_ALL_SUPPLIERS: 'upsell_SearchAll',
  CUSTOM_CROSS_SELL_SHOW: 'search_CustomCrossSell_Show',
  CUSTOM_CROSS_SELL_CLICK: 'search_CustomCrossSell_Click',
  MIXED_SPECIES_CROSS_SELL_SHOW: 'search_MixedSpeciesCrossSell_Show',
  MIXED_SPECIES_CROSS_SELL_CLICK: 'search_MixedSpeciesCrossSell_Click',
  MIXED_SPECIES_CREATIVE_CLICK: 'search_MixedSpeciesCreative_Click'
};

/*
    Helper functions to extract information for each of the actions
*/
const extractCampaignItemValues = (campaignItem, extra) => ({
  vendor_id: _.get(campaignItem, 'campaign.vendor.id'),
  vendor_name: _.get(campaignItem, 'campaign.vendor.name'),
  campaign_id: _.get(campaignItem, 'campaign.id'),
  campaign_name: _.get(campaignItem, 'campaign.name'),
  family_id: _.get(campaignItem, 'campaign.family.id'),
  family_name: _.get(campaignItem, 'campaign.family.name'),
  item_id: campaignItem.id,
  audience_id: _.get(campaignItem, 'offer.audience_id'),
  offer_id: _.get(campaignItem, 'offer.id'),
  offer_discount: campaignItem.discount / 100,
  offer_msrp: campaignItem.msrp / 100,
  offer_percentage: campaignItem.percentage,
  is_claimed: campaignItem.is_claimed,
  ...extra
});

const extractCampaignItemExtractInfoValues = (campaignItem, extra) => ({
  vendor_id: _.get(campaignItem, 'campaign.vendor.id'),
  vendor_name: _.get(campaignItem, 'campaign.vendor.name'),
  campaign_id: _.get(campaignItem, 'campaign.id'),
  campaign_name: _.get(campaignItem, 'campaign.name'),
  item_id: campaignItem.id,
  ...extra
});

const extractCreativeValues = (campaignItem, creative, extra) => ({
  ...extractCampaignItemValues(campaignItem),
  creative_id: creative.id,
  creative_kind: creative.kind,
  has_already_clicked: creative.has_clicked,
  ...extra
});

const extractCrossSellValues = (crossSell, campaign, extra) => ({
  campaign_id: crossSell.campaign_id,
  campaign_name: _.get(campaign, 'name'),
  vendor_name: _.get(campaign, 'vendor.name'),
  campaign_best_offer_percentage: _.get(campaign, 'details.best_offer_percentage'),
  family_id: _.get(campaign, 'family.id'),
  family_name: _.get(campaign, 'family.name'),
  cross_sell_id: crossSell.cross_sell_id,
  vendor_id: crossSell.vendor_id,
  ...extra
});

const extractCustomCrossSellValues = (item, extra) => ({
  item_id: _.get(item, 'id'),
  ...extra
});

const extractDealValues = (campaign, extra) => ({
  vendor_id: _.get(campaign, 'vendor.id'),
  vendor_name: _.get(campaign, 'vendor.name'),
  campaign_best_offer_percentage: _.get(campaign, 'details.best_offer_percentage'),
  campaign_is_new: _.get(campaign, 'details.is_new'),
  campaign_percent_spend_left: _.get(campaign, 'details.percent_spend_left'),
  campaign_days_left: _.get(campaign, 'details.days_left'),
  campaign_popularity: _.get(campaign, 'details.popularity'),
  campaign_previously_purchased: _.get(campaign, 'details.previously_purchased'),
  number_of_offers: _.size(_.get(campaign, 'offers')),
  campaign_id: _.get(campaign, 'id'),
  family_id: _.get(campaign, 'family.id'),
  family_name: _.get(campaign, 'family.name'),
  audience_id: _.get(campaign, ['offers', 0, 'audience_id']),
  campaign_name: _.get(campaign, 'name'),
  ...extra
});

const extractUpsellValues = (upsell, supply, extra) => ({
  upsell_item_id: _.get(supply, 'item_id'),
  upsell_supplier_id: _.get(supply, 'supplier_id'),
  upsell_supply_id: _.get(supply, 'id'),
  upsell_id: _.get(upsell, 'id'),
  campaign_ids: _.get(upsell, 'eligible_campaign_ids', []),
  title: _.get(upsell, 'title'),
  vendor_name: _.get(upsell, 'vendor_name'),
  ...extra
});

const extractMixedSpeciesValues = (campaignId, extra) => ({
  campaignId,
  ...extra
});

const extraUpsellCloseValues = (upsell, supply, start, end, extra) => extractUpsellValues(
  upsell,
  supply,
  {
    ...extra,
    secondsSpent: (_.get(end, 'time', 0) - _.get(start, 'time', 0)) / 1000,
  },
);

const extraUpsellAddToCartValues = (upsell, parentSupply, supply, extra) => ({
  ...extractUpsellValues(upsell, parentSupply),
  supply_id: _.get(supply, 'id'),
  item_id: _.get(supply, 'item_id'),
  price: _.get(supply, 'price', 0) / 100,
  discount: _.get(supply, 'discount', 0) / 100,
  supply_name: _.get(supply, 'name'),
  ...extra,
});

const analyticsActions = [{
  type: analyticsTypes.CAMPAIGN_CLAIM_SHOW,
  attributesExtractor: extractCampaignItemValues
}, {
  type: analyticsTypes.CAMPAIGN_CLAIM_CLICK,
  attributesExtractor: extractCampaignItemValues
}, {
  type: analyticsTypes.CAMPAIGN_CLAIM_CLAIM,
  attributesExtractor: extractCampaignItemValues
}, {
  type: analyticsTypes.CAMPAIGN_CLAIM_DISMISS,
  attributesExtractor: extractCampaignItemValues
}, {
  type: analyticsTypes.CAMPAIGN_CREATIVE_START,
  attributesExtractor: extractCreativeValues
}, {
  type: analyticsTypes.CAMPAIGN_CREATIVE_STOP,
  attributesExtractor: extractCreativeValues
}, {
  type: analyticsTypes.CAMPAIGN_CREATIVE_ADD_TO_CART,
  attributesExtractor: extractCreativeValues
}, {
  type: analyticsTypes.CAMPAIGN_CLAIM_COLLECT_INFO_SHOW,
  attributesExtractor: extractCampaignItemExtractInfoValues
}, {
  type: analyticsTypes.CAMPAIGN_CLAIM_COLLECT_INFO_CLICK,
  attributesExtractor: extractCampaignItemExtractInfoValues
}, {
  type: analyticsTypes.CAMPAIGN_CLAIM_COLLECT_INFO_SUBMIT,
  attributesExtractor: extractCampaignItemExtractInfoValues
}, {
  type: analyticsTypes.CAMPAIGN_CLAIM_COLLECT_INFO_DISMISS,
  attributesExtractor: extractCampaignItemExtractInfoValues
}, {
  type: analyticsTypes.CAMPAIGN_CROSS_SELL_SHOW,
  attributesExtractor: extractCrossSellValues
}, {
  type: analyticsTypes.CAMPAIGN_CROSS_SELL_CLICK,
  attributesExtractor: extractCrossSellValues
}, {
  type: analyticsTypes.CAMPAIGN_DEAL_VIEW,
  attributesExtractor: extractDealValues
}, {
  type: analyticsTypes.CAMPAIGN_DEAL_CLICK,
  attributesExtractor: extractDealValues
}, {
  type: analyticsTypes.UPSELL_SHOW,
  attributesExtractor: extractUpsellValues
}, {
  type: analyticsTypes.UPSELL_CLOSE,
  attributesExtractor: extraUpsellCloseValues,
}, {
  type: analyticsTypes.UPSELL_ADD_TO_CART,
  attributesExtractor: extraUpsellAddToCartValues
}, {
  type: analyticsTypes.UPSELL_SEARCH_ALL_SUPPLIERS,
  attributesExtractor: extractUpsellValues
}, {
  type: analyticsTypes.CUSTOM_CROSS_SELL_CLICK,
  attributesExtractor: extractCustomCrossSellValues
}, {
  type: analyticsTypes.CUSTOM_CROSS_SELL_SHOW,
  attributesExtractor: extractCustomCrossSellValues
}, {
  type: analyticsTypes.MIXED_SPECIES_CROSS_SELL_SHOW,
  attributesExtractor: extractMixedSpeciesValues
}, {
  type: analyticsTypes.MIXED_SPECIES_CROSS_SELL_CLICK,
  attributesExtractor: extractMixedSpeciesValues
}, {
  type: analyticsTypes.MIXED_SPECIES_CREATIVE_CLICK,
  attributesExtractor: extractMixedSpeciesValues
}];

export {
  analyticsTypes,
  analyticsActions
};
